// Profil d'une seance pour sa visualisation : suite d'etapes (efforts et
// recuperations) placees sur l'axe du temps, avec leur intensite (% de VMA).
// S'appuie sur la decomposition utilisee pour intervals.icu.
import { etapesSeance, type EtapeIcu } from './intervalsIcu.ts';
import { trouverZone, vitesseMoyenneMs } from './seanceEngine.ts';
import { type ZoneRow } from './allureTable.ts';

export type PartieProfil = 'echauffement' | 'corps' | 'recuperation';

export type SegmentProfil = {
  debutS: number;
  dureeS: number;
  distanceM: number;
  pctVMA: number;
  // Effort progressif ("30'@HP-10K") : intensite au debut et a la fin
  // (pctVMA = allure moyenne), et zone d'arrivee.
  pctVMADebut?: number;
  pctVMAFin?: number;
  codeFin?: string;
  // Zone de l'effort (recuperations : zone RECUP).
  code: string;
  nomZone: string;
  recup: boolean;
  partie: PartieProfil;
};

export type Profil = {
  segments: SegmentProfil[];
  dureeTotaleS: number;
  distanceTotaleM: number;
  // Debut et fin de chaque partie (echauffement, corps, recuperation du modele).
  parties: { partie: PartieProfil; debutS: number; finS: number }[];
};

// Recuperation trottinee par defaut si la table n'a pas de zone RECUP.
const PCT_RECUP_DEFAUT = 0.6;

function segment(e: EtapeIcu, vma: number, table: ZoneRow[], partie: PartieProfil, debutS: number): SegmentProfil {
  if (e.type === 'recup') {
    const zone = trouverZone(table, 'RECUP');
    const pct = zone && isFinite(zone.pctVMA) ? zone.pctVMA : PCT_RECUP_DEFAUT;
    const vitesse = (vma * pct) / 3.6;
    return {
      debutS,
      dureeS: e.secondes,
      distanceM: e.distanceM ?? e.secondes * vitesse,
      pctVMA: pct,
      code: 'RECUP',
      nomZone: 'Récupération',
      recup: true,
      partie,
    };
  }
  const zone = trouverZone(table, e.code);
  if (!zone || !isFinite(zone.pctVMA)) throw new Error(`Allure inconnue : ${e.code}`);
  const zoneFin = e.codeFin ? trouverZone(table, e.codeFin) : null;
  if (e.codeFin && (!zoneFin || !isFinite(zoneFin.pctVMA))) throw new Error(`Allure inconnue : ${e.codeFin}`);
  const vitesse = zoneFin ? vitesseMoyenneMs(vma, zone.pctVMA, zoneFin.pctVMA) : (vma * zone.pctVMA) / 3.6; // m/s
  const dureeS = e.secondes ?? (e.distanceM ?? 0) / vitesse;
  if (zoneFin) {
    return {
      debutS,
      dureeS,
      distanceM: e.distanceM ?? dureeS * vitesse,
      pctVMA: (vitesse * 3.6) / vma,
      pctVMADebut: zone.pctVMA,
      pctVMAFin: zoneFin.pctVMA,
      code: zone.code,
      codeFin: zoneFin.code,
      nomZone: `${zone.nom} → ${zoneFin.nom}`,
      recup: false,
      partie,
    };
  }
  return {
    debutS,
    dureeS,
    distanceM: e.distanceM ?? dureeS * vitesse,
    pctVMA: zone.pctVMA,
    code: zone.code,
    nomZone: zone.nom,
    recup: false,
    partie,
  };
}

/**
 * Profil d'une seance (corps, et echauffement / recuperation du modele s'il y
 * en a). Erreur si une notation n'est pas comprise ou si la VMA manque.
 */
export function profilSeance(
  notations: { echauffement?: string | null; corps: string; recuperation?: string | null },
  vma: number,
  table: ZoneRow[],
): Profil | { erreur: string } {
  if (!vma || vma <= 0) return { erreur: 'VMA inconnue : renseigne un record pour visualiser la séance.' };
  const parties: [PartieProfil, string | null | undefined][] = [
    ['echauffement', notations.echauffement],
    ['corps', notations.corps],
    ['recuperation', notations.recuperation],
  ];
  const profil: Profil = { segments: [], dureeTotaleS: 0, distanceTotaleM: 0, parties: [] };
  try {
    for (const [partie, notation] of parties) {
      if (!notation || !notation.trim()) continue;
      const debutPartie = profil.dureeTotaleS;
      for (const e of etapesSeance(notation, vma, table)) {
        const s = segment(e, vma, table, partie, profil.dureeTotaleS);
        if (!(s.dureeS > 0)) continue;
        profil.segments.push(s);
        profil.dureeTotaleS += s.dureeS;
        profil.distanceTotaleM += s.distanceM;
      }
      if (profil.dureeTotaleS > debutPartie) profil.parties.push({ partie, debutS: debutPartie, finS: profil.dureeTotaleS });
    }
  } catch (e) {
    return { erreur: (e as Error).message };
  }
  if (profil.segments.length === 0) return { erreur: 'Séance vide.' };
  return profil;
}

/** Allure (min:s/km) a un pourcentage de VMA. */
export function allureAuPct(vma: number, pct: number): string {
  const sParKm = Math.round(3600 / (vma * pct));
  return `${Math.floor(sParKm / 60)}:${String(sParKm % 60).padStart(2, '0')}/km`;
}
